import {
  AccountsAgencyPartnerChoices,
  CampaignCreativeTypeChoices,
  CampaignKpiTypeChoices,
  CampaignStatusChoices,
  DspChoices,
  InsightMetricsEnum,
  PrecisionCategoriesType,
  ProductCategoryChoices,
} from "../graphql/generated";
import { format } from "date-fns";
import { CostOptionsEnum } from "../pages/adminDashboardPage/optimus/optimusPerformanceGraphing/OptimusChartBox";
import {
  PublisherEnum,
  RangeEnum,
} from "../graphqlExternal/generated";

/**
 * Returns the dsp name to display in the interface. Built to work with DspChoices Enum
 * @param dsp
 */
export const getDSPName = (dsp: string): string => {
  switch (dsp) {
    case DspChoices.AmazonDsp:
      return "Amazon DSP";
    case DspChoices.Ttd:
      return "The Trade Desk";
    case DspChoices.Dv360:
      return "DV360";
    case DspChoices.ZetaDsp:
      return "Zeta DSP";
    case DspChoices.VerizonMedia:
      return "Y! DSP";
    case DspChoices.Centro:
      return "Basis";
    default:
      return formatTextDisplay(dsp);
  }
};

export const getPartnerName = (partner: string | undefined): string => {
  switch (partner) {
    case AccountsAgencyPartnerChoices.NewsUk:
    case PublisherEnum.NewsUk:
      return "News UK";
    case AccountsAgencyPartnerChoices.IrishTimes:
    case PublisherEnum.IrishTimes:
      return "Irish Times";
    case AccountsAgencyPartnerChoices.Arena:
    case PublisherEnum.Arena:
      return "Arena";
    case AccountsAgencyPartnerChoices.Pubmatic:
      return "Pubmatic";
    case AccountsAgencyPartnerChoices.Ttd:
      return "TTD";
    case AccountsAgencyPartnerChoices.Equativ:
      return "Equativ";
    default:
      if (!partner) {
        return "No partner found";
      }
      return formatTextDisplay(partner);
  }
};

export const getKPIDisplay = (kpi: string): string => {
  switch (kpi) {
    case CampaignKpiTypeChoices.WebsiteLands:
      return formatTextDisplay(kpi);
    case CampaignKpiTypeChoices.Viewability:
      return formatTextDisplay(kpi);
    case CampaignKpiTypeChoices.Reach:
      return formatTextDisplay(kpi);
    default:
      return kpi;
  }
};

export const getCreativeTypeDisplay = (creativeType: string): string => {
  switch (creativeType) {
    case CampaignCreativeTypeChoices.NotApplicable:
      return "N/A";
    default:
      return formatTextDisplay(creativeType);
  }
};

export const getMetricDisplay = (metric: string, title = false): string => {
  switch (metric) {
    case InsightMetricsEnum.Ctr:
      return "CTR";
    default:
      if (title) {
        return formatTextDisplay(metric);
      } else {
        return metric.toLowerCase();
      }
  }
};

export function formatTextDisplay(value: string): string {
  // First split the value on `_`
  const result = value.split("_");
  if (!result[1]) {
    return result[0][0] + result[0].slice(1).toLowerCase();
  } else {
    return result.map((r) => r[0] + r.slice(1).toLowerCase()).join(" ");
  }
}

export function removeUnderscores(value: string): string {
  const result = value.split("_");
  return result.join(" ");
}

export function formatDateDisplay(dateString: string): string {
  return format(new Date(dateString), "dd/MM/yy");
}

export function unCamelcase(value: string): string {
  return value
    .replace(/([A-Z]+)/g, " $1")
    .replace(/([A-Z][a-z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/[[iI]d]*$/, "ID");
}

export const getStatusColor = (status: string) => {
  switch (status) {
    case CampaignStatusChoices.Confirmed:
      return "green.300";
    case CampaignStatusChoices.Active:
      return "primary.dark";
    case CampaignStatusChoices.Archived:
    case CampaignStatusChoices.Complete:
      return "inactive";
    case CampaignStatusChoices.Cancelled:
      return "orange.300";

    default:
      return "primary.light";
  }
};

export const formatNumberDisplay = (value: number | string, suffix = "") => {
  return `${numberDisplaySI(value)}${suffix}`;
};

export const getMetricSuffix = (metric: InsightMetricsEnum): string => {
  switch (metric) {
    case InsightMetricsEnum.Ctr:
      return "%";
    default:
      return "";
  }
};

export const numberDisplaySI = (value: number | string, places = 2): string => {
  if (typeof value === "string") {
    value = parseInt(value);
  }
  const [result, power] = reduceNumber(value);
  switch (power) {
    case 0:
      return `${result}`;

    case 1:
      return `${result.toFixed(places)}k`;

    case 2:
      return `${result.toFixed(places)}M`;

    default:
      return `${value}`;
  }
};

const reduceNumber = (value: number, power = 0): number[] => {
  let result;

  if (Math.abs(value / 1000) < 1) {
    result = [value, power];
  } else {
    result = reduceNumber(value / 1000, power + 1);
  }
  return result;
};

export const formatPrecisionLabel = (
  precisionCategories: PrecisionCategoriesType[]
) => {
  const formattedPrecisonLabel = precisionCategories.map((category) => {
    let label = "";
    label += category.tier1;

    category.tier2 && (label += " > " + category.tier2);
    category.tier3 && (label += " > " + category.tier3);
    category.tier4 && (label += " > " + category.tier4);

    return label;
  });

  return formattedPrecisonLabel;
};

export const getProductLabel = (product: string) => {
  switch (product) {
    case ProductCategoryChoices.Compass:
      return "illuma Compass";
    case ProductCategoryChoices.Precision:
      return "illuma Precision";
    case ProductCategoryChoices.PrecisionCtv:
      return "illuma Precision (CTV)";
    case ProductCategoryChoices.PrecisionCustom:
      return "illuma Precision (Custom)";
    case ProductCategoryChoices.PrecisionSmart:
      return "illuma Precision (Smart)";
    case ProductCategoryChoices.Reacts:
      return "illuma Reacts";
    case ProductCategoryChoices.Smart:
      return "illuma Precision";
    case ProductCategoryChoices.SmartCtv:
      return "illuma Precision";
    default:
      return "illuma Product";
  }
};

interface precisionCategoryValueFormattingPropsInterface {
  categoryValue: string;
  keyValue?: string;
}

export const precisionCategoryValueFormatting = ({
  categoryValue,
  keyValue = "any",
}: precisionCategoryValueFormattingPropsInterface) => {
  const isNum = /^\d+\.\d+$/.test(categoryValue);

  if (isNum || keyValue.includes("MONEY") || keyValue.includes("DATE")) {
    return categoryValue.charAt(0).toUpperCase() + categoryValue.slice(1);
  } else {
    const removedNumbersValue = categoryValue.replace(/[0-9)(.)]/g, "");
    const preformatValue = removedNumbersValue.replace(/>/g, " > ");
    return preformatValue.charAt(0).toUpperCase() + preformatValue.slice(1);
  }
};

export enum PageTypeEnum {
  App = 'app',
  Web = 'web'
}

export interface pageTypeOptionsType {
  label: string;
  value: PageTypeEnum;
}

export enum LookbackOptionsEnum {
  Last_7Days = "LAST 7 DAYS",
  // Last_14Days = "LAST 14 DAYS",
  Last_30Days = "LAST 30 DAYS",
  Last_90Days = "LAST 90 DAYS",
}

export enum BsdLookbackOptionsEnum {
  Yesterday = "YESTERDAY",
  Last_7Days = "LAST 7 DAYS",
  Last_14Days = "LAST 14 DAYS",
  Last_30Days = "LAST 30 DAYS",
  Last_60Days = "LAST 60 DAYS",
  Previous_Month = "PREVIOUS MONTH",
  Previous_2Months = "PREVIOUS 2 MONTHS",
  Previous_3Months = "PREVIOUS 3 MONTHS",
}

export interface lookbackOptionsType {
  label: string;
  value: LookbackOptionsEnum;
}

export interface bsdLookbackOptionsType {
  label: string;
  value: BsdLookbackOptionsEnum;
}

// export interface bsdOverallLookbackOptionsType {
//   label: string;
//   value: BsdOveralllookbackOptionsEnum;
// }

export const getLookbackName = (lookback: string): string => {
  switch (lookback) {
    case LookbackOptionsEnum.Last_30Days:
      return "Last 30 days";
    case LookbackOptionsEnum.Last_7Days:
      return "Last 7 days";
    case LookbackOptionsEnum.Last_90Days:
      return "Last 90 days";
    // case LookbackOptionsEnum.Last_14Days:
    //     return "Last 14 days";
    default:
      return "Last 30 days";
  }
};

export const getBsdLookbackName = (lookback: string): string => {
  switch (lookback) {
    case BsdLookbackOptionsEnum.Yesterday:
      return "Yesterday";
    case BsdLookbackOptionsEnum.Last_7Days:
      return "Last 7 days";
    case BsdLookbackOptionsEnum.Last_14Days:
      return "Last 14 days";
    case BsdLookbackOptionsEnum.Last_30Days:
      return "Last 30 days";
    case BsdLookbackOptionsEnum.Last_60Days:
      return "Last 60 days";
    case BsdLookbackOptionsEnum.Previous_Month:
      return "Previous Month";
    case BsdLookbackOptionsEnum.Previous_2Months:
      return "Previous 2 Months";
    case BsdLookbackOptionsEnum.Previous_3Months:
      return "Previous 3 Months";
    default:
      return "Last 14 days";
  }
};

export const getCostName = (cost: string): string => {
  switch (cost) {
    case CostOptionsEnum.advertiser_cost_adv_currency:
      return "Advertiser Cost (Advertiser Currency)";
    case CostOptionsEnum.media_cost_adv_currency:
      return "Media Cost (Advertiser Currency)";
    case CostOptionsEnum.media_cost_usd:
      return "Media Cost (USD)";
    case CostOptionsEnum.advertiser_cost_usd:
      return "Advertiser Cost (USD)";
    default:
      return "Media Cost (Advertiser Currency)";
  }
};

export const getDateRangeLabel = (range: string): string => {
  switch (range) {
    case RangeEnum.Last_7Days:
      return "Last 7 days";
    // case RangeEnum.Last_14Days:
    //   return "Last 14 days";
    case RangeEnum.Last_30Days:
      return "Last 30 days";
    case RangeEnum.All:
      return "All";
    case RangeEnum.Custom:
      return "Custom";
    default:
      return "Last 7 days";
  }
};

export enum TempBayerAdvertiserChoicesEnum {
  Miralax_CH_US = "hduv1lw",
  Claritin_Base_CH_US = "gbxiyh3",
  Claritin_PEDS_CH_US = "vr3mq5d",
  Lotrimin_CH_US = "yf4evun",
  Alka_Seltzer_Plus_Cold_and_Flu_CH_US = "owv4fx2",
  Aleve_Base_CH_US = "x14vdpa"
}

export enum TempAppleAdvertiserChoicesEnum {
  Apple_Tv = "31aamh6",
}

export enum TempDemoAdvertiserChoicesEnum {
  DemoAdvertiser1 = "yf4evun",
  DemoAdvertiser2 = "x14vdpa",
  DemoAdvertiser3 = "hduv1lw", 
  DemoAdvertiser4 = "vr3mq5d",
  DemoAdvertiser5 = "gbxiyh3",

}

export const getBayerAdvertiserName = (advertiserId: string): string => {
  switch (advertiserId) {
    case TempBayerAdvertiserChoicesEnum.Miralax_CH_US:
      return "Miralax CH US";
    case TempBayerAdvertiserChoicesEnum.Alka_Seltzer_Plus_Cold_and_Flu_CH_US:
      return "Alka Seltzer Plus Cold and Flu CH US";
    case TempBayerAdvertiserChoicesEnum.Claritin_Base_CH_US:
      return "Claritin Base CH US";
    case TempBayerAdvertiserChoicesEnum.Claritin_PEDS_CH_US:
      return "Claritin PEDS CH US";
    case TempBayerAdvertiserChoicesEnum.Lotrimin_CH_US:
      return "Lotrimin CH US";
    case TempBayerAdvertiserChoicesEnum.Aleve_Base_CH_US:
      return "Aleve Base CH US";
    default:
      return "Unknown Advertiser";
  }
};

export const getAppleAdvertiserName = (advertiserId: string): string => {
  switch (advertiserId) {
    case TempAppleAdvertiserChoicesEnum.Apple_Tv:
      return "Apple TV";
    default:
      return "Unknown Advertiser";
  }
};

export const getDemoAdvertiserName = (advertiserId: string): string => {
  switch (advertiserId) {
    case TempDemoAdvertiserChoicesEnum.DemoAdvertiser1:
      return "Demo Advertiser 1";
    case TempDemoAdvertiserChoicesEnum.DemoAdvertiser2:
      return "Demo Advertiser 2";
    case TempDemoAdvertiserChoicesEnum.DemoAdvertiser3:
      return "Demo Advertiser 3";
    case TempDemoAdvertiserChoicesEnum.DemoAdvertiser4:
      return "Demo Advertiser 4";
    case TempDemoAdvertiserChoicesEnum.DemoAdvertiser5:
      return "Demo Advertiser 5";
    default:
      return "Unknown Advertiser";
  }
};
